import React from 'react';
import { Link } from 'react-router-dom';
import { CategoryIcon } from './icons';
import { HelpCategory, HelpArticleMeta } from './helpdeskApi';

interface CategoryViewProps {
    category: HelpCategory;
    articles: HelpArticleMeta[];
    relatedCategories: HelpCategory[];
}

/**
 * Single-category listing page — hero, article rows with intent pills, and
 * related-category chips. Ported from `categoryPage()` in
 * prototype/lib/templates.js. The prototype's "top tasks" sub-list depends on
 * per-category curated slugs (`cat.topTasks`) that are not part of the
 * backend contract for this phase, so it is intentionally omitted here (see
 * deviations in the final report) — the full article list still covers the
 * same content.
 */
const CategoryView: React.FC<CategoryViewProps> = ({ category, articles, relatedCategories }) => {
    return (
        <section className="hc-wrap hc-section hc-section--tight">
            <div className="hc-cat-hero">
                <span className="hc-cat-hero__icon">
                    <CategoryIcon name={category.icon} size={30} />
                </span>
                <div>
                    <h1>{category.title}</h1>
                    <p>{category.short}</p>
                </div>
            </div>

            <h2 className="hc-lead-h2">
                All articles <span className="hc-count">{articles.length}</span>
            </h2>

            {articles.length > 0 ? (
                <div className="hc-art-list">
                    {articles.map((article) => (
                        <Link key={article.url} className="hc-art-row" to={article.url}>
                            <span className="hc-art-row__main">
                                <span className="hc-art-row__title">{article.title}</span>
                                <span className="hc-art-row__sum">{article.summary || article.metaDescription}</span>
                            </span>
                            {article.intent && (
                                <span className={`hc-pill hc-pill--${article.intent}`}>{article.intent}</span>
                            )}
                        </Link>
                    ))}
                </div>
            ) : (
                <div className="hc-noresult">
                    <h2>No articles published in {category.title} yet.</h2>
                    <p>Check back soon, or contact support in the meantime.</p>
                </div>
            )}

            {relatedCategories.length > 0 && (
                <div className="hc-related-cats">
                    <h2 className="hc-lead-h2">Related categories</h2>
                    <div className="hc-chips">
                        {relatedCategories.map((related) => (
                            <Link key={related.slug} className="hc-chip" to={`/helpdesk/${related.slug}`}>
                                <CategoryIcon name={related.icon} size={16} />
                                {related.title}
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            <div className="hc-fallback">
                <p>Can&rsquo;t find what you need in {category.title}?</p>
                <Link className="hc-btn hc-btn--ghost" to="/helpdesk/support/contact-trafficcreator-support">
                    Contact support
                </Link>
            </div>
        </section>
    );
};

export default CategoryView;
